// Preferências do aluno que afetam o comportamento da IA (corretor + assistente).
// Persistidas em users.prefs (JSONB) no servidor sob duas chaves:
//   - prefs.aiCorrection → strictness, threshold, grammar, celebrações
//   - prefs.aiAssistant  → verbosity
//
// Valem APENAS pros modos Estudo e Simulação. Prova Agendada ignora.
//
// Estratégia de sincronização:
//   - Hidratadas no boot via /api/auth/me (campos aiCorrectionPrefs/aiAssistantPrefs).
//   - Mudanças do aluno chamam patchAccount({ prefs: { aiCorrection: {...} } }).
//     O backend faz shallow merge em users.prefs, então mandamos o sub-objeto
//     INTEIRO a cada save (o backend não mergeia profundo).

import { signal } from "@preact/signals";
import { api } from "./api";

export type CorrectionStrictness = "standard" | "strict";
export type AssistantVerbosity = "concise" | "standard" | "detailed";
export type GrammarMode = "off" | "judgmental" | "fixed";

export interface AiCorrectionPrefs {
  strictness: CorrectionStrictness;
  threshold: number; // 5..8
  grammarMode: GrammarMode;
  grammarPenalty: number; // 0.1..1.0
  celebrateDissertativeBlock: boolean;
  celebrateEssay: boolean;
}

export interface AiAssistantPrefs {
  verbosity: AssistantVerbosity;
}

/** Verbosidade do Tira-dúvida — independente do Assistente de Estudo. */
export interface QuickStudyPrefs {
  verbosity: AssistantVerbosity;
}

export const DEFAULT_CORRECTION_PREFS: AiCorrectionPrefs = {
  strictness: "standard",
  threshold: 7,
  grammarMode: "off",
  grammarPenalty: 0.3,
  celebrateDissertativeBlock: true,
  celebrateEssay: true
};

export const DEFAULT_ASSISTANT_PREFS: AiAssistantPrefs = {
  verbosity: "standard"
};

/** Tira-dúvida começa conciso (ferramenta de resposta rápida). */
export const DEFAULT_QUICK_STUDY_PREFS: QuickStudyPrefs = {
  verbosity: "concise"
};

/** Signals reativos hidratados no boot. Componentes leem
 *  `aiCorrectionPrefs.value.strictness` etc. */
export const aiCorrectionPrefs = signal<AiCorrectionPrefs>({
  ...DEFAULT_CORRECTION_PREFS
});
export const aiAssistantPrefs = signal<AiAssistantPrefs>({
  ...DEFAULT_ASSISTANT_PREFS
});
export const quickStudyPrefs = signal<QuickStudyPrefs>({
  ...DEFAULT_QUICK_STUDY_PREFS
});

// =============================================================================
// Hidratação / parse
// =============================================================================

export function parseCorrectionPrefs(raw: unknown): AiCorrectionPrefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_CORRECTION_PREFS };
  const o = raw as Record<string, unknown>;
  const strictness: CorrectionStrictness =
    o.strictness === "strict" || o.strictness === "standard"
      ? (o.strictness as CorrectionStrictness)
      : DEFAULT_CORRECTION_PREFS.strictness;
  const tRaw = typeof o.threshold === "number" ? o.threshold : Number(o.threshold);
  const threshold =
    Number.isFinite(tRaw) && tRaw >= 5 && tRaw <= 8
      ? Math.round(tRaw)
      : DEFAULT_CORRECTION_PREFS.threshold;
  const grammarMode: GrammarMode =
    o.grammarMode === "judgmental" || o.grammarMode === "fixed" || o.grammarMode === "off"
      ? (o.grammarMode as GrammarMode)
      : DEFAULT_CORRECTION_PREFS.grammarMode;
  const pRaw = typeof o.grammarPenalty === "number" ? o.grammarPenalty : Number(o.grammarPenalty);
  const grammarPenalty =
    Number.isFinite(pRaw) && pRaw >= 0.1 && pRaw <= 1
      ? Math.round(pRaw * 10) / 10
      : DEFAULT_CORRECTION_PREFS.grammarPenalty;
  return {
    strictness,
    threshold,
    grammarMode,
    grammarPenalty,
    celebrateDissertativeBlock:
      typeof o.celebrateDissertativeBlock === "boolean"
        ? o.celebrateDissertativeBlock
        : DEFAULT_CORRECTION_PREFS.celebrateDissertativeBlock,
    celebrateEssay:
      typeof o.celebrateEssay === "boolean"
        ? o.celebrateEssay
        : DEFAULT_CORRECTION_PREFS.celebrateEssay
  };
}

export function parseAssistantPrefs(raw: unknown): AiAssistantPrefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_ASSISTANT_PREFS };
  const o = raw as Record<string, unknown>;
  const verbosity: AssistantVerbosity =
    o.verbosity === "concise" || o.verbosity === "detailed" || o.verbosity === "standard"
      ? (o.verbosity as AssistantVerbosity)
      : DEFAULT_ASSISTANT_PREFS.verbosity;
  return { verbosity };
}

export function parseQuickStudyPrefs(raw: unknown): QuickStudyPrefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_QUICK_STUDY_PREFS };
  const o = raw as Record<string, unknown>;
  const verbosity: AssistantVerbosity =
    o.verbosity === "concise" || o.verbosity === "detailed" || o.verbosity === "standard"
      ? (o.verbosity as AssistantVerbosity)
      : DEFAULT_QUICK_STUDY_PREFS.verbosity;
  return { verbosity };
}

/** Chamada no App boot após /api/auth/me retornar. */
export function hydrateAiPrefs(payload: {
  aiCorrectionPrefs?: unknown;
  aiAssistantPrefs?: unknown;
  quickStudyPrefs?: unknown;
}) {
  if (payload.aiCorrectionPrefs) {
    aiCorrectionPrefs.value = parseCorrectionPrefs(payload.aiCorrectionPrefs);
  }
  if (payload.aiAssistantPrefs) {
    aiAssistantPrefs.value = parseAssistantPrefs(payload.aiAssistantPrefs);
  }
  if (payload.quickStudyPrefs) {
    quickStudyPrefs.value = parseQuickStudyPrefs(payload.quickStudyPrefs);
  }
}

// =============================================================================
// Setters — chamam patchAccount e atualizam o signal localmente em sucesso
// =============================================================================

async function saveCorrectionPrefs(next: AiCorrectionPrefs): Promise<void> {
  // Optimistic update — UI já reflete o novo valor enquanto o request voa.
  const previous = aiCorrectionPrefs.value;
  aiCorrectionPrefs.value = next;
  try {
    await api.patchAccount({ prefs: { aiCorrection: next } });
  } catch (err) {
    aiCorrectionPrefs.value = previous;
    throw err;
  }
}

async function saveAssistantPrefs(next: AiAssistantPrefs): Promise<void> {
  const previous = aiAssistantPrefs.value;
  aiAssistantPrefs.value = next;
  try {
    await api.patchAccount({ prefs: { aiAssistant: next } });
  } catch (err) {
    aiAssistantPrefs.value = previous;
    throw err;
  }
}

export function setStrictness(v: CorrectionStrictness): Promise<void> {
  return saveCorrectionPrefs({ ...aiCorrectionPrefs.value, strictness: v });
}

export function setThreshold(v: number): Promise<void> {
  const clamped = Math.max(5, Math.min(8, Math.round(v)));
  return saveCorrectionPrefs({ ...aiCorrectionPrefs.value, threshold: clamped });
}

export function setGrammarMode(v: GrammarMode): Promise<void> {
  return saveCorrectionPrefs({ ...aiCorrectionPrefs.value, grammarMode: v });
}

export function setGrammarPenalty(v: number): Promise<void> {
  const clamped = Math.max(0.1, Math.min(1.0, Math.round(v * 10) / 10));
  return saveCorrectionPrefs({ ...aiCorrectionPrefs.value, grammarPenalty: clamped });
}

export function setCelebrateDissertativeBlock(v: boolean): Promise<void> {
  return saveCorrectionPrefs({
    ...aiCorrectionPrefs.value,
    celebrateDissertativeBlock: v
  });
}

export function setCelebrateEssay(v: boolean): Promise<void> {
  return saveCorrectionPrefs({ ...aiCorrectionPrefs.value, celebrateEssay: v });
}

export function setVerbosity(v: AssistantVerbosity): Promise<void> {
  return saveAssistantPrefs({ ...aiAssistantPrefs.value, verbosity: v });
}

async function saveQuickStudyPrefs(next: QuickStudyPrefs): Promise<void> {
  const previous = quickStudyPrefs.value;
  quickStudyPrefs.value = next;
  try {
    await api.patchAccount({ prefs: { quickStudy: next } });
  } catch (err) {
    quickStudyPrefs.value = previous;
    throw err;
  }
}

export function setQuickStudyVerbosity(v: AssistantVerbosity): Promise<void> {
  return saveQuickStudyPrefs({ ...quickStudyPrefs.value, verbosity: v });
}

// =============================================================================
// Labels pra UI (badges, tooltips) — fonte única, não duplicar nos componentes
// =============================================================================

export function strictnessLabel(s: CorrectionStrictness): string {
  return s === "strict" ? "Rigoroso" : "Padrão";
}

export function strictnessDescription(s: CorrectionStrictness): string {
  switch (s) {
    case "strict":
      return "Cobra precisão extra e exige boas justificativas quando o enunciado pede explicação. Imprecisões menores descontam.";
    case "standard":
    default:
      return "Mede se a pergunta foi respondida. Citou o que pediu? Nota cheia. Sem cobrar justificativa quando o enunciado não pediu.";
  }
}

export function verbosityLabel(v: AssistantVerbosity): string {
  return v === "concise" ? "Conciso" : v === "detailed" ? "Detalhado" : "Padrão";
}

export function verbosityDescription(v: AssistantVerbosity): string {
  switch (v) {
    case "concise":
      return "Respostas curtas e diretas. 1-3 parágrafos.";
    case "detailed":
      return "Respostas profundas com analogias, exemplos numéricos e seções organizadas.";
    case "standard":
    default:
      return "Profundidade adaptativa. 2-4 parágrafos típicos.";
  }
}

export function grammarModeLabel(m: GrammarMode): string {
  switch (m) {
    case "judgmental":
      return "Norma culta: julgamental";
    case "fixed":
      return "Norma culta: valor fixo";
    case "off":
    default:
      return "Sem desconto de português";
  }
}
